/**
 * Pure helpers for the Project > Folder > Workspace hierarchy.
 *
 * A "project" is a root container (parentId === null); a "folder" lives inside a
 * project or another folder. Workspaces reference their container via `folderId`
 * (null = unfiled). Nothing here touches storage, so it is trivially testable.
 */

import type { WorkspaceFolder } from './workspaceFolders';

/** Project counts as level 1, so this allows a project plus four nested folder levels. */
export const MAX_FOLDER_DEPTH = 5;
export const MAX_FOLDER_NAME_LENGTH = 80;

export interface FolderNode {
    folder: WorkspaceFolder;
    children: FolderNode[];
    depth: number;
}

export interface FiledItem {
    folderId?: string | null;
}

const byName = (a: WorkspaceFolder, b: WorkspaceFolder) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

/** Trim, collapse whitespace and cap length. Returns '' for blank input. */
export const normalizeFolderName = (name: string): string =>
    name.replace(/\s+/g, ' ').trim().slice(0, MAX_FOLDER_NAME_LENGTH);

/** "Name" → "Name 2" → "Name 3"… until it no longer collides (case-insensitive); never exceeds the length cap. */
export const makeUniqueSiblingName = (name: string, siblingNames: string[]): string => {
    const taken = new Set(siblingNames.map(n => n.trim().toLowerCase()));
    const base = normalizeFolderName(name) || 'Untitled';
    if (!taken.has(base.toLowerCase())) return base;
    const withSuffix = (suffix: string) => base.slice(0, MAX_FOLDER_NAME_LENGTH - suffix.length).trimEnd() + suffix;
    for (let i = 2; i < 1000; i++) {
        const candidate = withSuffix(` ${i}`);
        if (!taken.has(candidate.toLowerCase())) return candidate;
    }
    return withSuffix(` ${Date.now()}`);
};

export const getFolderById = (folders: WorkspaceFolder[], id: string | null | undefined): WorkspaceFolder | undefined =>
    id ? folders.find(f => f.id === id) : undefined;

/**
 * Children grouped by parent id. Folders whose parent no longer exists are
 * treated as roots so they never silently disappear from the UI.
 */
/**
 * Effective parent of every folder: a missing parent becomes null (root), and one
 * member of each parent cycle (a → b → a) is promoted to the root so nothing
 * vanishes. Every other helper derives from this map so the sidebar tree, the
 * breadcrumb and the depth checks always agree.
 */
export const resolveParents = (folders: WorkspaceFolder[]): Map<string, string | null> => {
    const ids = new Set(folders.map(f => f.id));
    const parentOf = new Map<string, string | null>();
    for (const f of folders) {
        parentOf.set(f.id, f.parentId && ids.has(f.parentId) && f.parentId !== f.id ? f.parentId : null);
    }

    const reachable = new Set<string>();
    const markReachable = () => {
        const kids = new Map<string | null, string[]>();
        for (const [id, parent] of parentOf) {
            const list = kids.get(parent) || [];
            list.push(id);
            kids.set(parent, list);
        }
        const stack = [...(kids.get(null) || [])];
        while (stack.length) {
            const id = stack.pop() as string;
            if (reachable.has(id)) continue;
            reachable.add(id);
            stack.push(...(kids.get(id) || []));
        }
    };
    markReachable();
    for (const f of folders) {
        if (!reachable.has(f.id)) {
            parentOf.set(f.id, null);
            markReachable();
        }
    }
    return parentOf;
};

export const buildChildrenMap = (folders: WorkspaceFolder[]): Map<string | null, WorkspaceFolder[]> => {
    const parentOf = resolveParents(folders);
    const map = new Map<string | null, WorkspaceFolder[]>();
    for (const f of folders) {
        const parent = parentOf.get(f.id) ?? null;
        const list = map.get(parent) || [];
        list.push(f);
        map.set(parent, list);
    }
    for (const list of map.values()) list.sort(byName);
    return map;
};

/** Nested tree of folders, sorted by name at every level. Cycles are broken by a visited set. */
export const buildFolderTree = (folders: WorkspaceFolder[]): FolderNode[] => {
    const childrenMap = buildChildrenMap(folders);
    const visited = new Set<string>();
    const build = (parentId: string | null, depth: number): FolderNode[] =>
        (childrenMap.get(parentId) || [])
            .filter(f => !visited.has(f.id))
            .map(f => {
                visited.add(f.id);
                return { folder: f, depth, children: build(f.id, depth + 1) };
            });
    return build(null, 1);
};

/** Root → … → the folder itself. Empty when the id is unknown. Uses the same resolved parents as the tree. */
export const getFolderPath = (folders: WorkspaceFolder[], id: string | null | undefined): WorkspaceFolder[] => {
    const path: WorkspaceFolder[] = [];
    if (!id) return path;
    const parentOf = resolveParents(folders);
    const seen = new Set<string>();
    let current = getFolderById(folders, id);
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        path.unshift(current);
        current = getFolderById(folders, parentOf.get(current.id) ?? null);
    }
    return path;
};

/** 1 for a project, 2 for a folder directly inside it, etc. 0 when unknown. */
export const getFolderDepth = (folders: WorkspaceFolder[], id: string | null | undefined): number =>
    getFolderPath(folders, id).length;

/** The project (root container) a folder belongs to, or the folder itself if it is a project. */
export const getProjectForFolder = (folders: WorkspaceFolder[], id: string | null | undefined): WorkspaceFolder | undefined =>
    getFolderPath(folders, id)[0];

/** Every folder id below the given one (not including itself). */
export const getDescendantFolderIds = (folders: WorkspaceFolder[], id: string): Set<string> => {
    const childrenMap = buildChildrenMap(folders);
    const result = new Set<string>();
    const stack = [id];
    while (stack.length) {
        const current = stack.pop() as string;
        for (const child of childrenMap.get(current) || []) {
            if (!result.has(child.id)) {
                result.add(child.id);
                stack.push(child.id);
            }
        }
    }
    return result;
};

export const isSameOrDescendant = (folders: WorkspaceFolder[], candidateId: string, ancestorId: string): boolean =>
    candidateId === ancestorId || getDescendantFolderIds(folders, ancestorId).has(candidateId);

/** Height of a subtree: 1 for a leaf folder, 2 if it has children, etc. */
export const getSubtreeHeight = (folders: WorkspaceFolder[], id: string): number => {
    const childrenMap = buildChildrenMap(folders);
    const visited = new Set<string>();
    const height = (folderId: string): number => {
        if (visited.has(folderId)) return 0;
        visited.add(folderId);
        const kids = childrenMap.get(folderId) || [];
        return 1 + (kids.length ? Math.max(...kids.map(k => height(k.id))) : 0);
    };
    return height(id);
};

export interface MoveCheck {
    ok: boolean;
    reason?: string;
}

/** Can a new folder be created under `parentId` (null = a new project at the root)? */
export const canCreateFolderIn = (folders: WorkspaceFolder[], parentId: string | null): MoveCheck => {
    if (parentId === null) return { ok: true };
    const parent = getFolderById(folders, parentId);
    if (!parent) return { ok: false, reason: 'That folder no longer exists.' };
    if (getFolderDepth(folders, parentId) + 1 > MAX_FOLDER_DEPTH) {
        return { ok: false, reason: `Folders can only be nested ${MAX_FOLDER_DEPTH - 1} levels deep.` };
    }
    return { ok: true };
};

/**
 * Can `folderId` be moved under `newParentId`? Rejects moving a project (roots stay
 * roots), moving into itself or a descendant, and moving deeper than the cap.
 */
export const canMoveFolder = (folders: WorkspaceFolder[], folderId: string, newParentId: string | null): MoveCheck => {
    const folder = getFolderById(folders, folderId);
    if (!folder) return { ok: false, reason: 'That folder no longer exists.' };
    if (folder.kind === 'project') return { ok: false, reason: 'Projects always live at the top level.' };
    if (newParentId === null) return { ok: false, reason: 'Folders must live inside a project.' };
    if (newParentId === folder.parentId) return { ok: false, reason: 'The folder is already there.' };
    if (isSameOrDescendant(folders, newParentId, folderId)) {
        return { ok: false, reason: 'A folder cannot be moved into itself.' };
    }
    const parentDepth = getFolderDepth(folders, newParentId);
    if (parentDepth === 0) return { ok: false, reason: 'That folder no longer exists.' };
    if (parentDepth + getSubtreeHeight(folders, folderId) > MAX_FOLDER_DEPTH) {
        return { ok: false, reason: `Folders can only be nested ${MAX_FOLDER_DEPTH - 1} levels deep.` };
    }
    return { ok: true };
};

/** Workspaces filed directly in the folder (not in sub-folders). */
export const countDirectWorkspaces = (items: FiledItem[], folderId: string | null): number =>
    items.reduce((n, w) => n + ((w.folderId ?? null) === folderId ? 1 : 0), 0);

/**
 * Recursive workspace counts for every folder in one pass:
 * a folder's count includes everything in its sub-folders (what the sidebar shows).
 */
export const getWorkspaceCountsByFolder = (folders: WorkspaceFolder[], items: FiledItem[]): Map<string, number> =>
    sumByFolder(folders, items, () => 1);

/**
 * What the workspaces of every folder cost together, its sub-folders' included: what the
 * library shows beside a project (a workspace's cost is its History's, see utils/historyItems.ts).
 */
export const getWorkspaceCostsByFolder = <T extends FiledItem & { totalCost?: number }>(folders: WorkspaceFolder[], items: T[]): Map<string, number> =>
    sumByFolder(folders, items, item => item.totalCost ?? 0);

/** A value of each workspace added up per folder, its sub-folders' included, in one pass */
const sumByFolder = <T extends FiledItem>(folders: WorkspaceFolder[], items: T[], valueOf: (item: T) => number): Map<string, number> => {
    const direct = new Map<string, number>();
    const ids = new Set(folders.map(f => f.id));
    for (const w of items) {
        const fid = w.folderId ?? null;
        if (fid && ids.has(fid)) direct.set(fid, (direct.get(fid) || 0) + valueOf(w));
    }
    const childrenMap = buildChildrenMap(folders);
    const totals = new Map<string, number>();
    const visiting = new Set<string>();
    const total = (id: string): number => {
        if (totals.has(id)) return totals.get(id) as number;
        if (visiting.has(id)) return 0;
        visiting.add(id);
        let n = direct.get(id) || 0;
        for (const child of childrenMap.get(id) || []) n += total(child.id);
        visiting.delete(id);
        totals.set(id, n);
        return n;
    };
    for (const f of folders) total(f.id);
    return totals;
};

/** Recursive count for one folder. */
export const countWorkspacesInFolder = (folders: WorkspaceFolder[], items: FiledItem[], folderId: string): number =>
    getWorkspaceCountsByFolder(folders, items).get(folderId) || 0;

/** Ids of every workspace filed in the folder or any of its sub-folders. */
export const collectWorkspaceIdsInFolder = <T extends FiledItem & { id: string }>(folders: WorkspaceFolder[], items: T[], folderId: string): T[] => {
    const scope = getDescendantFolderIds(folders, folderId);
    scope.add(folderId);
    return items.filter(w => !!w.folderId && scope.has(w.folderId));
};
